import type { ReactNode } from "react";

type SettingsPanelTone = "default" | "danger";

interface SettingsPageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  meta?: ReactNode;
  action?: ReactNode;
  className?: string;
}

interface SettingsPanelProps {
  title?: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  tone?: SettingsPanelTone;
  className?: string;
  contentClassName?: string;
}

interface SettingsFieldProps {
  label: string;
  description?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}

interface SettingsFieldGroupProps {
  children: ReactNode;
  columns?: 1 | 2;
  className?: string;
}

interface SettingsActionBarProps {
  children: ReactNode;
  hint?: ReactNode;
  className?: string;
}

interface SettingsSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
}

const panelToneClasses: Record<SettingsPanelTone, string> = {
  default:
    "border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,255,255,0.9))] shadow-[0_1px_0_rgba(255,255,255,0.8),0_24px_70px_-38px_rgba(15,23,42,0.22)]",
  danger:
    "border-red-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,246,246,0.96))] shadow-[0_1px_0_rgba(255,255,255,0.8),0_24px_70px_-42px_rgba(153,27,27,0.20)]",
};

export function SettingsPageHeader({
  eyebrow,
  title,
  description,
  meta,
  action,
  className = "",
}: SettingsPageHeaderProps) {
  return (
    <div
      className={`flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between ${className}`}
    >
      <div className="min-w-0 space-y-2">
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
            {eyebrow}
          </p>
        )}
        <div className="space-y-1.5">
          <h1 className="text-[28px] font-semibold tracking-[-0.03em] text-primary">
            {title}
          </h1>
          {description && (
            <p className="max-w-[640px] text-[14px] leading-6 text-secondary">
              {description}
            </p>
          )}
        </div>
        {meta && <div>{meta}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function SettingsPanel({
  title,
  description,
  children,
  action,
  footer,
  tone = "default",
  className = "",
  contentClassName = "",
}: SettingsPanelProps) {
  const hasHeader = title || description || action;

  return (
    <section
      className={`overflow-hidden rounded-[24px] border backdrop-blur-sm ${panelToneClasses[tone]} ${className}`}
    >
      {hasHeader && (
        <div className="flex flex-col gap-4 border-b border-border/80 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
          <div className="min-w-0">
            {title && (
              <h2 className="text-[17px] font-semibold tracking-[-0.02em] text-primary">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 max-w-[620px] text-[13px] leading-6 text-secondary">
                {description}
              </p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      <div className={`px-5 py-5 sm:px-6 sm:py-6 ${contentClassName}`}>
        {children}
      </div>
      {footer && (
        <div className="border-t border-border/80 px-5 py-4 sm:px-6">
          {footer}
        </div>
      )}
    </section>
  );
}

export function SettingsField({
  label,
  description,
  error,
  children,
  className = "",
}: SettingsFieldProps) {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="space-y-1">
        <span className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-secondary">
          {label}
        </span>
        {description && (
          <p className="text-[13px] leading-5 text-secondary">{description}</p>
        )}
      </div>
      {children}
      {error && <p className="text-[12px] text-red-600">{error}</p>}
    </div>
  );
}

export function SettingsFieldGroup({
  children,
  columns = 2,
  className = "",
}: SettingsFieldGroupProps) {
  return (
    <div
      className={`grid gap-5 ${columns === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"} ${className}`}
    >
      {children}
    </div>
  );
}

export function SettingsActionBar({
  children,
  hint,
  className = "",
}: SettingsActionBarProps) {
  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      <div className="min-h-[20px] text-[13px] text-secondary">{hint}</div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function SettingsSection({
  title,
  description,
  children,
  action,
}: SettingsSectionProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 px-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-[20px] font-semibold tracking-[-0.03em] text-primary">
            {title}
          </h2>
          {description && (
            <p className="mt-1 max-w-[640px] text-[13px] leading-6 text-secondary">
              {description}
            </p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <SettingsPanel>{children}</SettingsPanel>
    </div>
  );
}
